import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import { roundFCFA } from '@shared/utils/round';

export interface DAS1Line {
  matricule: string;
  nom_complet: string;
  brut_annuel: number;
  irpp_annuel: number;
  cnss_sal_annuel: number;
  cnamgs_sal_annuel: number;
}

export interface DAS1Result {
  annee: string;
  lignes: DAS1Line[];
  total_brut: number;
  total_irpp: number;
}

/**
 * Génération de la DAS 1 (Déclaration Annuelle des Salaires).
 * Obligation employeur OHADA + CGI Gabon, à transmettre à la DGI avant le 30 avril N+1.
 */
export class DASService {
  static genererDAS1(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    annee: string,
  ): DAS1Result {
    const salMap = new Map(salaries.map((s) => [s.id, s]));
    const lignes = new Map<string, DAS1Line>();

    for (const b of bulletins) {
      if (b.statut !== 'valide' && b.statut !== 'paye') continue;
      if (!b.periode.startsWith(annee)) continue;
      const sal = salMap.get(b.salarie_id);
      if (!sal) continue;
      let line = lignes.get(b.salarie_id);
      if (!line) {
        line = {
          matricule: sal.matricule,
          nom_complet: `${sal.prenom} ${sal.nom}`,
          brut_annuel: 0,
          irpp_annuel: 0,
          cnss_sal_annuel: 0,
          cnamgs_sal_annuel: 0,
        };
        lignes.set(b.salarie_id, line);
      }
      line.brut_annuel += b.brut_imposable;
      line.irpp_annuel += b.irpp_mensuel;
      line.cnss_sal_annuel += b.cnss_vid_sal;
      line.cnamgs_sal_annuel += b.cnamgs_sal;
    }

    const arr = Array.from(lignes.values()).map((l) => ({
      ...l,
      brut_annuel: roundFCFA(l.brut_annuel),
      irpp_annuel: roundFCFA(l.irpp_annuel),
      cnss_sal_annuel: roundFCFA(l.cnss_sal_annuel),
      cnamgs_sal_annuel: roundFCFA(l.cnamgs_sal_annuel),
    }));

    return {
      annee,
      lignes: arr,
      total_brut: arr.reduce((s, l) => s + l.brut_annuel, 0),
      total_irpp: arr.reduce((s, l) => s + l.irpp_annuel, 0),
    };
  }
}
